import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MatterCostCodeBudgetService, { CostCodeBudgetMap } from "@frontend/AppSettings/services/MatterCostCodeBudgetService";
import BillingCategoryService from "@frontend/AppSettings/services/BillingCategoryService";
import { BillingCategory } from "@frontend/Models/Rate/RateModels";
import useCurrentEmployee from "@frontend/AppSettings/hooks/useCurrentEmployee";
import toastService from "@frontend/AppSettings/services/ToastService";
import "./EstimatePanel.css";

interface EstimateRow {
  code: string;
  description: string;
  budget: number;
  spent: number;
  hasBudget: boolean;
}

const fmt = (n: number): string => {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

/**
 * Matter Financial → Estimate/Budget panel. Shows the per-cost-code breakdown (budget vs spent) and
 * lets an admin edit each code's budget. The overall project budget is simply the SUM of these
 * per-code budgets (matters.budget is derived, kept in sync server-side by upsert_budgets), so the
 * total row is the project total — there is nothing to reconcile.
 */
const EstimatePanel: React.FC = () => {
  const { matterId } = useParams();
  const currentEmployee = useCurrentEmployee();
  const permishNum =
    typeof currentEmployee?.permish_id === "string"
      ? parseInt(currentEmployee.permish_id, 10)
      : currentEmployee?.permish_id;
  const canEdit = permishNum === 1;

  const [budgets, setBudgets] = useState<CostCodeBudgetMap>({});
  const [categories, setCategories] = useState<BillingCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!matterId) return;
    setLoading(true);
    try {
      const [budgetMap, cats] = await Promise.all([
        MatterCostCodeBudgetService.getBudgets(matterId).catch(() => ({} as CostCodeBudgetMap)),
        BillingCategoryService.getCategories(true).catch(() => [] as BillingCategory[]),
      ]);
      setBudgets(budgetMap);
      setCategories(Array.isArray(cats) ? cats : []);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    load();
  }, [load]);

  // One row per catalog code, plus any code that carries a budget or hours but isn't in the active
  // catalog (e.g. a retired code that still has spend) — so nothing with money silently disappears.
  const rows = useMemo<EstimateRow[]>(() => {
    const descByCode = new Map<string, string>();
    categories.forEach((c) => descByCode.set(c.categoryid, c.category || ""));
    const codes = new Set<string>();
    categories.forEach((c) => codes.add(c.categoryid));
    Object.keys(budgets).forEach((c) => codes.add(c));
    return Array.from(codes)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map((code) => {
        const b = budgets[code];
        return {
          code,
          description: descByCode.get(code) ?? "",
          budget: b?.budget ?? 0,
          spent: b?.group ?? 0,
          hasBudget: !!b && b.budget > 0,
        };
      });
  }, [categories, budgets]);

  const sumCodeBudgets = useMemo(
    () => rows.reduce((sum, r) => sum + (r.budget || 0), 0),
    [rows],
  );

  const startEdit = (code: string, current: number) => {
    setDraft(current > 0 ? String(current) : "");
    setEditingCode(code);
  };

  const saveEdit = async (code: string) => {
    if (!matterId || saving) return;
    const trimmed = draft.trim();
    const parsed = trimmed === "" ? 0 : parseFloat(trimmed);
    if (trimmed !== "" && (Number.isNaN(parsed) || parsed < 0)) {
      toastService.warning("Enter a valid budget in hours (0 or more).");
      return;
    }
    setSaving(true);
    try {
      await MatterCostCodeBudgetService.saveBudgets(matterId, [
        { cost_code: code, budget_hours: parsed },
      ]);
      setBudgets((prev) => ({
        ...prev,
        [code]: { budget: parsed, group: prev[code]?.group ?? 0 },
      }));
      setEditingCode(null);
      toastService.success("Cost code budget updated");
    } catch (e: any) {
      console.error("Error saving cost code budget:", e);
      const serverMsg = e?.data?.error || e?.data?.message;
      toastService.error(
        e?.status === 403
          ? serverMsg || "Only administrators can change cost code budgets."
          : serverMsg || e?.message || "Failed to update cost code budget."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="estimate-panel estimate-panel-empty">Loading estimate…</div>;
  }

  return (
    <div className="estimate-panel">
      <h3 className="estimate-panel-title">Estimate / Budget</h3>
      <div className="estimate-banner estimate-banner-neutral">
        The overall project budget is the sum of the cost code budgets below: {fmt(sumCodeBudgets)} hrs.
      </div>
      {rows.length === 0 ? (
        <div className="estimate-panel-empty">
          No cost codes configured yet. Add codes under Team → Cost Codes.
        </div>
      ) : (
        <table className="estimate-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Description</th>
              <th>Budget (hrs)</th>
              <th>Spent (hrs)</th>
              <th>%</th>
              {canEdit && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const percent = r.hasBudget ? Math.round((r.spent / r.budget) * 100) : null;
              const over = r.hasBudget && r.spent > r.budget;
              const isEditing = editingCode === r.code;
              return (
                <tr key={r.code} className={over ? "estimate-row-over" : ""}>
                  <td className="estimate-code">{r.code}</td>
                  <td>{r.description || "—"}</td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        step="any"
                        className="estimate-edit-input"
                        value={draft}
                        autoFocus
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit(r.code);
                          else if (e.key === "Escape") setEditingCode(null);
                        }}
                      />
                    ) : r.hasBudget ? (
                      fmt(r.budget)
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{fmt(r.spent)}</td>
                  <td className={over ? "estimate-percent-over" : ""}>
                    {percent != null ? `${percent}%` : "—"}
                  </td>
                  {canEdit && (
                    <td>
                      {isEditing ? (
                        <div className="estimate-actions">
                          <button className="estimate-save-btn" onClick={() => saveEdit(r.code)} disabled={saving}>
                            Save
                          </button>
                          <button className="estimate-cancel-btn" onClick={() => setEditingCode(null)}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button className="estimate-edit-btn" onClick={() => startEdit(r.code, r.budget)}>
                          Edit
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} className="estimate-total-label">Total budget</td>
              <td className="estimate-total-value">{fmt(sumCodeBudgets)}</td>
              <td colSpan={canEdit ? 3 : 2} />
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
};

export default EstimatePanel;
